import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type {
  AssetRecord,
  Collection,
  JobRecord,
  RootRecord,
  SavedSearch
} from "@focalfolio/domain-model";
import { matchesQuery } from "@focalfolio/application-core";

interface CatalogDb extends DBSchema {
  roots: {
    key: string;
    value: RootRecord;
    indexes: { "by-name": string };
  };
  assets: {
    key: string;
    value: AssetRecord;
    indexes: {
      "by-root": string;
      "by-parent": [string, string];
      "by-path": [string, string];
      "by-kind": string;
      "by-rating": number;
      "by-updated": number;
    };
  };
  collections: {
    key: string;
    value: Collection;
    indexes: { "by-kind": string; "by-name": string };
  };
  searches: {
    key: string;
    value: SavedSearch;
    indexes: { "by-name": string };
  };
  jobs: {
    key: string;
    value: JobRecord;
    indexes: { "by-status": string; "by-updated": number };
  };
  settings: {
    key: string;
    value: { key: string; value: unknown; updatedAt: number };
  };
  migrations: {
    key: number;
    value: { version: number; appliedAt: number; description: string };
  };
}

export interface AssetQuery {
  rootId?: string;
  parentPath?: string;
  text?: string;
  kinds?: string[];
  minimumRating?: number;
  labels?: string[];
  extensions?: string[];
  sort?: "filename" | "captureDate" | "size" | "lastModified" | "rating";
  direction?: "asc" | "desc";
  limit?: number;
  offset?: number;
  queryTree?: import("@focalfolio/domain-model").QueryTree;
}

export interface CatalogBackup {
  format: "focalfolio-catalog";
  version: 1;
  exportedAt: number;
  applicationVersion: string;
  roots: RootRecord[];
  assets: AssetRecord[];
  collections: Collection[];
  savedSearches: SavedSearch[];
  settings: Array<{ key: string; value: unknown; updatedAt: number }>;
}

const DB_NAME = "focalfolio-catalog-v2";
const DB_VERSION = 1;

function searchableText(asset: AssetRecord): string {
  const metadata = asset.metadata;
  return [
    asset.filename,
    asset.relativePath,
    asset.extension,
    asset.kind,
    metadata.title,
    metadata.description,
    metadata.creator,
    metadata.copyright,
    metadata.cameraMake,
    metadata.cameraModel,
    metadata.lens,
    ...metadata.keywords
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("en-US");
}

function compareAssets(a: AssetRecord, b: AssetRecord, sort: AssetQuery["sort"]): number {
  switch (sort) {
    case "captureDate":
      return (a.metadata.captureDate ?? "").localeCompare(b.metadata.captureDate ?? "");
    case "size":
      return a.size - b.size;
    case "lastModified":
      return a.lastModified - b.lastModified;
    case "rating":
      return a.rating - b.rating;
    case "filename":
    default:
      return a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: "base" });
  }
}

export class LocalCatalog {
  private databasePromise: Promise<IDBPDatabase<CatalogDb>> | undefined;

  private database(): Promise<IDBPDatabase<CatalogDb>> {
    this.databasePromise ??= openDB<CatalogDb>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const roots = database.createObjectStore("roots", { keyPath: "id" });
        roots.createIndex("by-name", "name");

        const assets = database.createObjectStore("assets", { keyPath: "id" });
        assets.createIndex("by-root", "rootId");
        assets.createIndex("by-parent", ["rootId", "parentPath"]);
        assets.createIndex("by-path", ["rootId", "relativePath"], { unique: true });
        assets.createIndex("by-kind", "kind");
        assets.createIndex("by-rating", "rating");
        assets.createIndex("by-updated", "updatedAt");

        const collections = database.createObjectStore("collections", { keyPath: "id" });
        collections.createIndex("by-kind", "kind");
        collections.createIndex("by-name", "name");

        const searches = database.createObjectStore("searches", { keyPath: "id" });
        searches.createIndex("by-name", "name");

        const jobs = database.createObjectStore("jobs", { keyPath: "id" });
        jobs.createIndex("by-status", "status");
        jobs.createIndex("by-updated", "updatedAt");

        database.createObjectStore("settings", { keyPath: "key" });
        database.createObjectStore("migrations", { keyPath: "version" });
      },
      blocked() {
        console.warn("A previous FocalFolio tab is holding the catalog open.");
      }
    }).then(async (database) => {
      const current = await database.get("migrations", 1);
      if (!current) {
        await database.put("migrations", { version: 1, appliedAt: Date.now(), description: "Initial catalog schema" });
      }
      return database;
    });
    return this.databasePromise;
  }

  async requestPersistentStorage(): Promise<{ persisted: boolean; quota?: number; usage?: number }> {
    const persisted = (await navigator.storage?.persist?.()) ?? false;
    const estimate = await navigator.storage?.estimate?.();
    return { persisted, ...(estimate?.quota ? { quota: estimate.quota } : {}), ...(estimate?.usage ? { usage: estimate.usage } : {}) };
  }

  async putRoot(root: RootRecord): Promise<void> {
    const database = await this.database();
    await database.put("roots", root);
  }

  async getRoot(id: string): Promise<RootRecord | undefined> {
    return (await this.database()).get("roots", id);
  }

  async getRoots(): Promise<RootRecord[]> {
    return (await this.database()).getAll("roots");
  }

  async removeRoot(id: string, options: { removeAssets?: boolean } = {}): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(["roots", "assets"], "readwrite");
    await transaction.objectStore("roots").delete(id);
    if (options.removeAssets) {
      const index = transaction.objectStore("assets").index("by-root");
      let cursor = await index.openKeyCursor(IDBKeyRange.only(id));
      while (cursor) {
        await transaction.objectStore("assets").delete(cursor.primaryKey);
        cursor = await cursor.continue();
      }
    }
    await transaction.done;
  }

  async upsertAssets(records: AssetRecord[]): Promise<void> {
    if (records.length === 0) return;
    const database = await this.database();
    const transaction = database.transaction("assets", "readwrite", { durability: "strict" });
    for (const record of records) await transaction.store.put(record);
    await transaction.done;
  }

  async putAsset(record: AssetRecord): Promise<void> {
    await (await this.database()).put("assets", record);
  }

  async getAsset(id: string): Promise<AssetRecord | undefined> {
    return (await this.database()).get("assets", id);
  }

  async getAssetByPath(rootId: string, relativePath: string): Promise<AssetRecord | undefined> {
    return (await this.database()).getFromIndex("assets", "by-path", [rootId, relativePath]);
  }

  async deleteAsset(id: string): Promise<void> {
    await (await this.database()).delete("assets", id);
  }

  async markMissingAssets(rootId: string, discoveredPaths: Set<string>): Promise<number> {
    const database = await this.database();
    const transaction = database.transaction("assets", "readwrite");
    const index = transaction.store.index("by-root");
    let cursor = await index.openCursor(IDBKeyRange.only(rootId));
    let changed = 0;
    while (cursor) {
      const missing = !discoveredPaths.has(cursor.value.relativePath);
      if (Boolean(cursor.value.missing) !== missing) {
        await cursor.update({ ...cursor.value, missing, updatedAt: Date.now() });
        changed += 1;
      }
      cursor = await cursor.continue();
    }
    await transaction.done;
    return changed;
  }

  async queryAssets(query: AssetQuery = {}): Promise<{ assets: AssetRecord[]; total: number }> {
    const database = await this.database();
    let records: AssetRecord[];
    if (query.rootId && query.parentPath !== undefined) {
      records = await database.getAllFromIndex("assets", "by-parent", [query.rootId, query.parentPath]);
    } else if (query.rootId) {
      records = await database.getAllFromIndex("assets", "by-root", query.rootId);
    } else {
      records = await database.getAll("assets");
    }

    const text = query.text?.trim().toLocaleLowerCase("en-US");
    records = records.filter((asset) => {
      if (asset.missing) return false;
      if (text && !searchableText(asset).includes(text)) return false;
      if (query.kinds?.length && !query.kinds.includes(asset.kind)) return false;
      if (query.minimumRating !== undefined && asset.rating < query.minimumRating) return false;
      if (query.labels?.length && !query.labels.includes(asset.label)) return false;
      if (query.extensions?.length && !query.extensions.includes(asset.extension)) return false;
      if (query.queryTree && !matchesQuery(asset, query.queryTree)) return false;
      return true;
    });

    records.sort((a, b) => compareAssets(a, b, query.sort ?? "filename") * (query.direction === "desc" ? -1 : 1));
    const total = records.length;
    const offset = Math.max(0, query.offset ?? 0);
    const limit = Math.max(1, Math.min(query.limit ?? 5000, 25_000));
    return { assets: records.slice(offset, offset + limit), total };
  }

  async putCollection(collection: Collection): Promise<void> {
    await (await this.database()).put("collections", collection);
  }

  async getCollections(): Promise<Collection[]> {
    return (await this.database()).getAll("collections");
  }

  async deleteCollection(id: string): Promise<void> {
    await (await this.database()).delete("collections", id);
  }

  async putSavedSearch(search: SavedSearch): Promise<void> {
    await (await this.database()).put("searches", search);
  }

  async getSavedSearches(): Promise<SavedSearch[]> {
    return (await this.database()).getAll("searches");
  }

  async deleteSavedSearch(id: string): Promise<void> {
    await (await this.database()).delete("searches", id);
  }

  async putJob(job: JobRecord): Promise<void> {
    await (await this.database()).put("jobs", job);
  }

  async getJobs(): Promise<JobRecord[]> {
    const jobs = await (await this.database()).getAll("jobs");
    return jobs.sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async getRecoverableJobs(): Promise<JobRecord[]> {
    const jobs = await this.getJobs();
    return jobs.filter((job) => ["planned", "preflighted", "running", "verifying", "needs-user-action"].includes(job.status));
  }

  async setSetting(key: string, value: unknown): Promise<void> {
    await (await this.database()).put("settings", { key, value, updatedAt: Date.now() });
  }

  async getSetting<T>(key: string, fallback: T): Promise<T> {
    const record = await (await this.database()).get("settings", key);
    return (record?.value as T | undefined) ?? fallback;
  }

  async exportBackup(applicationVersion = "0.2.0"): Promise<CatalogBackup> {
    const database = await this.database();
    const [roots, assets, collections, savedSearches, settings] = await Promise.all([
      database.getAll("roots"),
      database.getAll("assets"),
      database.getAll("collections"),
      database.getAll("searches"),
      database.getAll("settings")
    ]);
    return {
      format: "focalfolio-catalog",
      version: 1,
      exportedAt: Date.now(),
      applicationVersion,
      roots: roots.map((root) => ({ ...root, permission: "unknown" })),
      assets,
      collections,
      savedSearches,
      settings
    };
  }

  async importBackup(backup: CatalogBackup, mode: "merge" | "replace" = "merge"): Promise<void> {
    if (backup.format !== "focalfolio-catalog" || backup.version !== 1) throw new Error("Unsupported catalog backup format.");
    const database = await this.database();
    const stores: Array<"roots" | "assets" | "collections" | "searches" | "settings"> = ["roots", "assets", "collections", "searches", "settings"];
    const transaction = database.transaction(stores, "readwrite", { durability: "strict" });
    if (mode === "replace") {
      await Promise.all(stores.map((store) => transaction.objectStore(store).clear()));
    }
    for (const root of backup.roots) await transaction.objectStore("roots").put({ ...root, permission: "unknown" });
    for (const asset of backup.assets) await transaction.objectStore("assets").put(asset);
    for (const collection of backup.collections) await transaction.objectStore("collections").put(collection);
    for (const search of backup.savedSearches) await transaction.objectStore("searches").put(search);
    for (const setting of backup.settings) await transaction.objectStore("settings").put(setting);
    await transaction.done;
  }

  async reset(): Promise<void> {
    const database = await this.database();
    const stores: Array<"roots" | "assets" | "collections" | "searches" | "jobs" | "settings"> = ["roots", "assets", "collections", "searches", "jobs", "settings"];
    const transaction = database.transaction(stores, "readwrite", { durability: "strict" });
    await Promise.all(stores.map((store) => transaction.objectStore(store).clear()));
    await transaction.done;
  }
}

export const catalog = new LocalCatalog();
